import { useState } from "react";
import { Link } from "react-router";
import { Statement } from "../components/Showcase";
import { HeroShots } from "../components/Screens";
import { useReveal } from "../hooks";

/*
 * HandVero Zuhause: die Ansicht für Privatpersonen. Eigene Optik (Hellblau, siehe styles.css, html[data-mode="privat"]),
 * eigene Inhalte, aber dieselbe Software. Texte bewusst einfach und ehrlich, ohne Fachjargon.
 */

const STATEMENT = "Ihr Zuhause hat *Leitungen, *Geräte und *Papiere. HandVero hält sie in einer *Akte zusammen: mit *Fotos, *Typenschildern und *Erinnerungen.";

export const ERKLAERT: { q: string; a: string[] }[] = [
  {
    q: "Warum soll man die Elektrik im Haus prüfen lassen?",
    a: [
      "Eine Elektroanlage altert still. Kontakte lockern sich, Isolierungen werden spröde, Schutzschalter lösen irgendwann nicht mehr sicher aus. Man merkt es erst, wenn etwas passiert: ein Brand oder ein Stromschlag.",
      "Eine feste gesetzliche Pflicht für selbst bewohnte Häuser gibt es nicht. Vermieter müssen die Anlage aber in sicherem Zustand halten, und viele Versicherungen fragen nach einem Nachweis, wenn es einen Schaden gab. Als Faustregel gelten etwa vier Jahre zwischen zwei Prüfungen, bei älteren Anlagen eher kürzer.",
      "Gut zu wissen: Die Prüfung ist keine Reparatur. Sie zeigt nur, ob alles sicher ist. Das Protokoll bekommen Sie schriftlich, und das gehört in Ihre Akte.",
    ],
  },
  {
    q: "Warum verkauft einem der Heizungsbauer eine jährliche Wartung?",
    a: [
      "Die ehrliche Antwort: Teils, weil es sich lohnt, teils, weil es ein Geschäft ist. Eine gesetzliche Pflicht zur jährlichen Wartung gibt es für Gas- und Ölheizungen in der Regel nicht. Pflicht ist die Abgasmessung durch den Schornsteinfeger, in festen Abständen.",
      "Sinnvoll ist die Wartung trotzdem: Ein gereinigter und richtig eingestellter Kessel verbraucht weniger, fällt seltener im Winter aus und hält länger. Oft verlangt auch die Herstellergarantie einen Wartungsnachweis. Und wer zur Miete wohnt, schaut in den Mietvertrag.",
      "Tipp: Fragen Sie, was genau gemacht wird, und lassen Sie sich das schriftlich geben. Den Nachweis legen Sie in der Akte beim Gerät ab.",
    ],
  },
  {
    q: "Wozu ist der FI-Schutzschalter und warum den Prüfknopf drücken?",
    a: [
      "Der FI-Schutzschalter schaltet in Bruchteilen einer Sekunde ab, wenn Strom dort fließt, wo er nicht soll, zum Beispiel durch einen Menschen. Das kann Leben retten.",
      "Wie jedes Bauteil kann er mit der Zeit klemmen. Darum empfehlen die Hersteller, die Prüftaste regelmäßig zu drücken, meist alle sechs Monate, manche monatlich. Steht ein Intervall auf Ihrem Gerät, richten Sie sich danach. Beim Drücken der Taste „T“ muss er sofort auslösen. Tut er es nicht, rufen Sie eine Elektrofachkraft.",
    ],
  },
  {
    q: "Wie oft sollte man Rauchmelder prüfen?",
    a: [
      "Rauchmelder haben eine Prüftaste. Einmal im Jahr drücken, bis der Signalton kommt, genügt für den Alltag. Die Batterie hält bei den meisten Geräten bis zu zehn Jahre. Danach ist der ganze Melder zu ersetzen, das Datum steht auf der Rückseite.",
      "In allen Bundesländern sind Rauchmelder in Schlaf- und Kinderzimmern sowie Fluren Pflicht. Wer zuständig ist, Eigentümer oder Mieter, regelt das jeweilige Landesrecht.",
    ],
  },
];

function Frage({ q, a }: { q: string; a: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? " open" : ""}`}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{q}</span>
        <i aria-hidden="true">{open ? "–" : "+"}</i>
      </button>
      {open && (
        <div className="faq-body">
          {a.map((t, i) => (
            <p key={i}>{t}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export function Privat() {
  useReveal();
  return (
    <section data-page="privat" className="fun">
      <div className="hero2">
        <div className="wrap hero2-grid">
          <div className="hero2-copy">
            <span className="kicker">▼ HandVero Zuhause</span>
            <h1 className="mega">
              <span className="ln1">
                <span>Haus.</span>
              </span>{" "}
              <span className="ln1">
                <span>Akte.</span>
              </span>{" "}
              <span className="ln1">
                <span className="outline">Fertig.</span>
              </span>
            </h1>
            <p className="hero2-lead">
              Fotos von Leitungen, Typenschilder Ihrer Geräte, Rechnungen und Wartungstermine: alles an einem Ort, am Handy zur Hand und für Handwerker per Link zu teilen.
            </p>
            <div className="hero-actions">
              <Link className="btn btn-white" to="/privat/registrieren">
                Kostenlos starten
              </Link>
              <a className="btn btn-line" href="#typenschild">
                So geht's
              </a>
            </div>
          </div>
          <div className="hero2-visual">
            <HeroShots />
          </div>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>01</span>
          <span>▼ Was es ist</span>
        </div>
        <Statement text={STATEMENT} />
      </div>

      <div className="wrap chapter" id="typenschild">
        <div className="kick">
          <span>02</span>
          <span>▼ Typenschild fotografieren</span>
        </div>
        <div className="proof-head">
          <span className="tag go">Zum Ausprobieren</span>
          <h2>Foto vom Typenschild. Die Daten stehen im Ordner.</h2>
          <p className="lead">
            Sie fotografieren das Schild an Heizung, Waschmaschine oder Wallbox. HandVero liest Hersteller, Typ, Seriennummer und Baujahr heraus und legt sie als Gerätekarte
            in Ihrer Akte ab, schriftlich, mit dem Foto dabei. Was falsch gelesen wurde, ändern Sie mit einem Fingertipp.
          </p>
        </div>
        <div className="bigsteps">
          <div className="bigstep reveal">
            <b>1</b>
            <div>
              <h3>Gerät wählen</h3>
              <p>Zum Beispiel „Heizung“ im Ordner Keller.</p>
            </div>
          </div>
          <div className="bigstep reveal">
            <b>2</b>
            <div>
              <h3>Typenschild fotografieren</h3>
              <p>Vero sagt Ihnen, wie Sie das Schild gut ins Bild bekommen.</p>
            </div>
          </div>
          <div className="bigstep reveal">
            <b>3</b>
            <div>
              <h3>Prüfen und speichern</h3>
              <p>Die gelesenen Daten kontrollieren, speichern, fertig. Das Foto bleibt dabei.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>03</span>
          <span>▼ Vero erklärt</span>
        </div>
        <div className="proof-head">
          <h2>Ehrliche Antworten, kein Geschwafel.</h2>
          <p className="lead">Warum Elektrik geprüft wird, was eine Heizungswartung bringt und worauf Sie achten sollten. Kurz und ohne Verkaufsdruck.</p>
        </div>
        <div className="faq">
          {ERKLAERT.map((f) => (
            <Frage key={f.q} {...f} />
          ))}
        </div>
        <p className="note-small">Allgemeine Information, keine Rechtsberatung. Für Ihren Einzelfall fragen Sie eine Fachkraft.</p>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>04</span>
          <span>▼ Das gehört in die Akte</span>
        </div>
        <div className="feat4">
          <div className="feat">
            <span className="mono">Leitungen</span>
            <h3>Fotos vor dem Verputzen</h3>
            <p>Wo liegt welche Leitung? Ein Foto mit Notiz, Jahre später Gold wert.</p>
          </div>
          <div className="feat">
            <span className="mono">Geräte</span>
            <h3>Typenschilder und Handbücher</h3>
            <p>Heizung, Boiler, Wallbox, Waschmaschine: Daten, Garantie und Rechnung am Gerät.</p>
          </div>
          <div className="feat">
            <span className="mono">Nachweise</span>
            <h3>Protokolle und Wartungen</h3>
            <p>Prüfprotokoll, Schornsteinfeger, Heizungswartung: alles an einem Platz.</p>
          </div>
          <div className="feat">
            <span className="mono">Termine</span>
            <h3>Erinnerungen</h3>
            <p>Rauchmelder, Heizung, Prüfung: Sie bekommen einen Hinweis, bevor es fällig ist.</p>
          </div>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="cta-mega">
          <div className="kick light">
            <span>05</span>
            <span>▼ Jetzt starten</span>
          </div>
          <div className="stack">
            <h2>Zum Start kostenlos.</h2>
            <p>Sie brauchen nur Ihre E-Mail-Adresse. Ihre Daten liegen auf Servern in Deutschland, ohne Tracker. Werbung gäbe es höchstens später und nur mit Ankündigung.</p>
            <div className="hero-actions">
              <Link className="btn btn-white" to="/privat/registrieren">
                Kostenlos registrieren
              </Link>
              <Link className="btn btn-line" to="/">
                Ich bin ein Betrieb
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
